import {
  collection, doc, setDoc, deleteDoc, getDoc, getDocs, onSnapshot,
  query, orderBy, limit, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

// ── BASKET ───────────────────────────────────────────────────────────────────
// Products a customer or a designer has set aside, at users/{uid}/basket/{id}.
//
// A SAVED LIST, NOT A CHECKOUT. Nothing here takes payment — a shop's listing
// links out to its own page, and that is where a purchase happens. So this is
// the marketplace equivalent of a wishlist: the things you found on Tunify and
// want to come back to.
//
// THE DOCUMENT ID IS THE PHOTO ID. That makes adding idempotent — pressing the
// button twice cannot produce two rows — and makes "is this already in my
// basket" a direct get rather than a query.
//
// THE ROWS ARE DELIBERATELY TINY: a photo id, the shop that published it, and
// when it was added. NO THUMBNAIL AND NO PRICE, and both omissions are the
// point:
//
//   · A thumbnail is a ~30KB data URL (Storage is not provisioned — see
//     galleryService). Copying one into every basket row would make a listener
//     over a 100-item basket a multi-megabyte subscription, re-sent on every
//     change. Small rows are what make the id subscription below affordable.
//   · A copied PRICE goes stale the moment the shop changes it, and a basket
//     that quotes yesterday's number is worse than one that quotes none.
//
// So the basket page reads each product from `photos` when it opens. That costs
// one read per row, on a page opened occasionally — and it buys a price that is
// always current, plus an accurate answer when a product has been taken down:
// the rules refuse a hidden item and the row renders as unavailable rather than
// as a listing that no longer exists.

const MAX_ITEMS = 100;

const col = (uid) => collection(db, 'users', uid, 'basket');
const ref = (uid, photoId) => doc(db, 'users', uid, 'basket', photoId);

/** Adds a product. Idempotent — the photo id IS the document id. */
export async function addToBasket(uid, item) {
  if (!uid || !item?.id) return false;
  await setDoc(ref(uid, item.id), {
    photoId: item.id,
    // Who published it, so the basket can link back to the shop without
    // re-reading the product first.
    shopUid: item.ownerUid || '',
    addedAt: serverTimestamp(),
  });
  return true;
}

export async function removeFromBasket(uid, photoId) {
  if (!uid || !photoId) return false;
  await deleteDoc(ref(uid, photoId));
  return true;
}

/** One direct get. Used only where the id subscription is not available. */
export async function isInBasket(uid, photoId) {
  if (!uid || !photoId) return false;
  const snap = await getDoc(ref(uid, photoId));
  return snap.exists();
}

/**
 * Live set of photo ids in the basket.
 *
 * Every basket button on a page reads from ONE subscription rather than each
 * doing its own get — a lightbox paging through twenty products would
 * otherwise be twenty reads to draw twenty icons. Cheap precisely because the
 * rows carry nothing but ids.
 *
 * Returns an unsubscribe, or a no-op when signed out.
 */
export function subscribeBasketIds(uid, cb) {
  if (!uid) { cb(new Set()); return () => {}; }
  return onSnapshot(
    query(col(uid), limit(MAX_ITEMS)),
    (snap) => cb(new Set(snap.docs.map(d => d.id))),
    // A refused or dropped subscription must not leave the buttons stuck in a
    // half-state; an empty set renders them all as "not added", which is the
    // safe reading.
    () => cb(new Set()),
  );
}

/** The rows themselves, newest first. The basket page resolves the products. */
export async function fetchBasketRows(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(col(uid), orderBy('addedAt', 'desc'), limit(MAX_ITEMS)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Empties the basket. Batched, since a hundred individual deletes is a hundred
 * round trips — and Firestore caps a batch at 500 writes, comfortably above
 * MAX_ITEMS.
 */
export async function clearBasket(uid) {
  if (!uid) return 0;
  const snap = await getDocs(query(col(uid), limit(MAX_ITEMS)));
  if (snap.empty) return 0;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  return snap.size;
}

export { MAX_ITEMS as BASKET_MAX };
